/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PERMISSIONS - Feature Gating & Tier Validation
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Backend permission checking for subscription-based feature access.
 * 
 * RESPONSIBILITIES:
 * - Validate user's subscription tier before executing protected functions
 * - Check feature access (cloud database, payments, exports, etc.)
 * - Enforce usage limits (invoices/month, products, customers)
 * - Throw descriptive errors when access is denied
 * - Handle subscription status edge cases (inactive, expired, etc.)
 * 
 * SECURITY MODEL:
 * - All protected database operations MUST call permission checks
 * - Frontend routing is convenience - backend validation is security
 * - Fail-closed: deny access on errors or missing data
 * - Never trust frontend - always validate on backend
 * 
 * USAGE IN OPERATIONS:
 * import { requireFeature, requireLimit } from '../core/permissions.js';
 * 
 * export async function createInvoice(invoiceData) {
 *   await requireFeature('cloud_database_access');  // throws if basic tier
 *   await requireLimit('invoices_per_month');       // throws if limit exceeded
 *   // ... rest of function
 * }
 * 
 * VERSION: 1.0.0
 * LAST UPDATED: 2024
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "./supabase.js";
import { 
  canCreateInvoice, 
  canCreateProduct, 
  canCreateCustomer,
  getUserSubscription 
} from "../operations/subscription.js";
import { FEATURES, LIMITS, getRequiredTierName } from "../features/featureList.js";

// ══════════════════════════════════════════════════════════════════════════════
// CUSTOM ERROR CLASSES
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Thrown when user's tier doesn't have access to a feature
 * 
 * @property {string} code - Error code 'PERMISSION_DENIED'
 * @property {string} feature - Feature key that was denied
 * @property {string} userTier - User's current tier name
 * @property {string} requiredTier - Minimum tier name required
 * @property {number} userTierLevel - User's tier level (0, 1, 2)
 * 
 * @example
 * catch (error) {
 *   if (error.code === 'PERMISSION_DENIED') {
 *     showUpgradeModal(error.requiredTier);
 *   }
 * }
 */
export class PermissionDeniedError extends Error {
  constructor(feature, userTier, requiredTier, userTierLevel = 0) {
    const message = `Access denied: Feature '${feature}' requires ${requiredTier} tier or higher. Current tier: ${userTier}`;
    super(message);
    
    this.name = 'PermissionDeniedError';
    this.code = 'PERMISSION_DENIED';
    this.feature = feature;
    this.userTier = userTier;
    this.requiredTier = requiredTier;
    this.userTierLevel = userTierLevel;
    
    // Maintain stack trace (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PermissionDeniedError);
    }
  }
}

/**
 * Thrown when user has exceeded their usage limit
 * 
 * @property {string} code - Error code 'LIMIT_EXCEEDED'
 * @property {string} limitType - Type of limit exceeded
 * @property {number} current - Current usage count
 * @property {number} max - Maximum allowed (null if unlimited)
 * @property {number} remaining - How many remaining (0 if exceeded)
 * 
 * @example
 * catch (error) {
 *   if (error.code === 'LIMIT_EXCEEDED') {
 *     alert(`You've used ${error.current} of ${error.max} ${error.limitType}`);
 *   }
 * }
 */
export class LimitExceededError extends Error {
  constructor(limitType, current, max, remaining = 0, userMessage = null) {
    const displayType = limitType.replace(/_/g, ' ');
    const message = userMessage || `Limit exceeded: ${current}/${max === null ? '∞' : max} ${displayType}`;
    super(message);
    
    this.name = 'LimitExceededError';
    this.code = 'LIMIT_EXCEEDED';
    this.limitType = limitType;
    this.current = current;
    this.max = max;
    this.remaining = remaining;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, LimitExceededError);
    }
  }
}

/**
 * Thrown when subscription status prevents access
 * 
 * @property {string} code - Error code 'SUBSCRIPTION_INACTIVE'
 * @property {string} status - Subscription status (inactive, expired, cancelled)
 * @property {string} action - Suggested action for user
 * 
 * @example
 * catch (error) {
 *   if (error.code === 'SUBSCRIPTION_INACTIVE') {
 *     showReactivateModal(error.status);
 *   }
 * }
 */
export class SubscriptionInactiveError extends Error {
  constructor(status, tierName) {
    const message = `Subscription is ${status}. Please renew your ${tierName} subscription to continue.`;
    super(message);
    
    this.name = 'SubscriptionInactiveError';
    this.code = 'SUBSCRIPTION_INACTIVE';
    this.status = status;
    this.tierName = tierName;
    this.action = status === 'inactive' ? 'renew' : 'reactivate';
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SubscriptionInactiveError);
    }
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// TIER & SUBSCRIPTION RETRIEVAL
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get user's current subscription tier with status handling
 * 
 * Handles subscription status logic:
 * - active/trial: Full access to tier features
 * - inactive: 7-day grace period, then drop to basic
 * - expired: Basic tier only
 * - cancelled: Basic tier only
 * - no record: Default to basic tier
 * 
 * @returns {Promise<object>} Subscription object with tier info
 * @throws {Error} On database errors
 * 
 * @example
 * const tier = await getUserTier();
 * console.log(tier.tierName);    // "Professional"
 * console.log(tier.tierLevel);   // 1
 * console.log(tier.features);    // { cloud_database_access: true, ... }
 */
export async function getUserTier() {
  try {
    // Use existing RPC function from subscription.js
    const subscription = await getUserSubscription();
    
    // ─────────────────────────────────────────────────────────────────────────
    // HANDLE SUBSCRIPTION STATUS
    // ─────────────────────────────────────────────────────────────────────────
    
    const status = subscription.status;
    
    // Active or trial - full access
    if (status === 'active' || status === 'trial') {
      return subscription;
    }
    
    // Inactive - check grace period (7 days)
    if (status === 'inactive') {
      const periodEnd = new Date(subscription.currentPeriodEnd);
      const gracePeriodEnd = new Date(periodEnd.getTime() + 7 * 24 * 60 * 60 * 1000); // +7 days
      const now = new Date();
      
      if (now <= gracePeriodEnd) {
        // Still in grace period - keep tier access but add warning flag
        return {
          ...subscription,
          gracePeriod: true,
          gracePeriodEndsAt: gracePeriodEnd.toISOString()
        };
      }
      
      // Grace period expired - drop to basic tier
      return {
        ...subscription,
        tierId: 'basic',
        tierName: 'Basic',
        tierLevel: 0,
        monthlyPrice: 0,
        features: subscription.features, // RPC will return basic features
        downgradedFrom: subscription.tierName,
        downgradeReason: 'grace_period_expired'
      };
    }
    
    // Expired or cancelled - basic tier only
    if (status === 'expired' || status === 'cancelled') {
      return {
        ...subscription,
        tierId: 'basic',
        tierName: 'Basic',
        tierLevel: 0,
        monthlyPrice: 0,
        features: subscription.features, // RPC will return basic features
        downgradedFrom: subscription.tierName,
        downgradeReason: status
      };
    }
    
    // Unknown status - fail safe to basic tier
    console.warn(`Unknown subscription status: ${status}. Defaulting to basic tier.`);
    return {
      ...subscription,
      tierId: 'basic',
      tierName: 'Basic',
      tierLevel: 0,
      monthlyPrice: 0,
      features: {},
      downgradedFrom: subscription.tierName,
      downgradeReason: 'unknown_status'
    };
    
  } catch (error) {
    console.error('Failed to get user tier:', error);
    
    // Fail closed - default to basic tier (safest)
    return {
      tierId: 'basic',
      tierName: 'Basic',
      tierLevel: 0,
      monthlyPrice: 0,
      status: 'unknown',
      features: {},
      usage: {
        invoicesThisMonth: 0,
        invoiceLimit: 10,
        productsTotal: 0,
        productLimit: 5,
        customersTotal: 0,
        customerLimit: 5
      },
      error: error.message,
      downgradedFrom: null,
      downgradeReason: 'error'
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// FEATURE CHECKING (Boolean Checks)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user has access to a specific feature (non-throwing)
 * 
 * Use this for conditional UI rendering or soft checks.
 * For hard permission enforcement, use requireFeature() instead.
 * 
 * @param {string} featureKey - Feature key from FEATURES constant
 * @returns {Promise<boolean>} True if user has the feature, false otherwise
 * 
 * @example
 * // Conditional UI rendering
 * const canExport = await hasFeature(FEATURES.CSV_EXPORT);
 * if (canExport) {
 *   showExportButton();
 * }
 */
export async function hasFeature(featureKey) {
  try {
    const tier = await getUserTier();
    
    // Check if feature exists in tier's features object
    return tier.features[featureKey] === true;
    
  } catch (error) {
    console.error(`Failed to check feature ${featureKey}:`, error);
    // Fail closed - deny access on error
    return false;
  }
}

/**
 * Check if user's tier level meets minimum requirement (non-throwing)
 * 
 * Use for simple tier-level checks without specific feature validation.
 * 
 * @param {number} minTierLevel - Minimum tier level required (0, 1, or 2)
 * @returns {Promise<boolean>} True if user's tier >= minTierLevel
 * 
 * @example
 * // Check if user is Professional or higher
 * const isPro = await hasTierLevel(1);
 */
export async function hasTierLevel(minTierLevel) {
  try {
    const tier = await getUserTier();
    return tier.tierLevel >= minTierLevel;
  } catch (error) {
    console.error(`Failed to check tier level ${minTierLevel}:`, error);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// PERMISSION ENFORCEMENT (Throwing Checks)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Require user to have a specific feature - throws if not available
 * 
 * Use this at the start of protected functions to enforce access control.
 * This is your main permission gate for feature-based restrictions.
 * 
 * @param {string} featureKey - Feature key from FEATURES constant
 * @throws {PermissionDeniedError} If user doesn't have the feature
 * @throws {SubscriptionInactiveError} If subscription is inactive (outside grace period)
 * 
 * @example
 * export async function exportToCSV(data) {
 *   await requireFeature(FEATURES.CSV_EXPORT);  // Throws if not Exclusive tier
 *   // ... proceed with export
 * }
 */
export async function requireFeature(featureKey) {
  const tier = await getUserTier();
  
  // ─────────────────────────────────────────────────────────────────────────
  // CHECK SUBSCRIPTION STATUS (outside grace period)
  // ─────────────────────────────────────────────────────────────────────────
  
  if (tier.downgradeReason && !tier.gracePeriod) {
    // Subscription is inactive and not in grace period
    const status = tier.status || tier.downgradeReason;
    throw new SubscriptionInactiveError(status, tier.downgradedFrom || tier.tierName);
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // CHECK FEATURE ACCESS
  // ─────────────────────────────────────────────────────────────────────────
  
  const hasAccess = tier.features[featureKey] === true;
  
  if (!hasAccess) {
    const requiredTier = getRequiredTierName(featureKey);
    
    throw new PermissionDeniedError(
      featureKey,
      tier.tierName,
      requiredTier,
      tier.tierLevel
    );
  }
  
  // Feature check passed - return tier info for convenience
  return tier;
}

/**
 * Require user's tier level to meet minimum - throws if insufficient
 * 
 * Use when you need tier-level checks without tying to a specific feature.
 * 
 * @param {number} minTierLevel - Minimum tier level required (0, 1, or 2)
 * @throws {PermissionDeniedError} If user's tier < minTierLevel
 * 
 * @example
 * export async function advancedFeature() {
 *   await requireTierLevel(2);  // Requires Exclusive tier
 *   // ... proceed
 * }
 */
export async function requireTierLevel(minTierLevel) {
  const tier = await getUserTier();
  
  if (tier.tierLevel < minTierLevel) {
    const tierNames = ['Basic', 'Professional', 'Exclusive'];
    const requiredTierName = tierNames[minTierLevel] || 'Unknown';
    
    throw new PermissionDeniedError(
      `tier_level_${minTierLevel}`,
      tier.tierName,
      requiredTierName,
      tier.tierLevel
    );
  }
  
  return tier;
}

// ══════════════════════════════════════════════════════════════════════════════
// LIMIT ENFORCEMENT (Usage Quotas)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Require user to be within usage limit - throws if exceeded
 * 
 * Use this before create operations to enforce quotas.
 * Leverages existing RPC functions (canCreateInvoice, etc.) for limit checks.
 * 
 * @param {string} limitKey - Limit key from LIMITS constant
 * @throws {LimitExceededError} If user has exceeded their limit
 * @throws {Error} If limitKey is unknown
 * 
 * @example
 * export async function createInvoice(invoiceData) {
 *   await requireLimit(LIMITS.INVOICES_PER_MONTH);  // Throws if limit exceeded
 *   // ... proceed with creation
 * }
 */
export async function requireLimit(limitKey) {
  let check;
  
  // ─────────────────────────────────────────────────────────────────────────
  // CALL APPROPRIATE LIMIT CHECK FUNCTION
  // ─────────────────────────────────────────────────────────────────────────
  
  switch (limitKey) {
    case LIMITS.INVOICES_PER_MONTH:
      check = await canCreateInvoice();
      break;
      
    case LIMITS.PRODUCTS_LIFETIME:
      check = await canCreateProduct();
      break;
      
    case LIMITS.CUSTOMERS_LIFETIME:
      check = await canCreateCustomer();
      break;
      
    default:
      throw new Error(`Unknown limit type: ${limitKey}`);
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // THROW ERROR IF LIMIT EXCEEDED
  // ─────────────────────────────────────────────────────────────────────────
  
  if (!check.allowed) {
    throw new LimitExceededError(
      limitKey,
      check.current,
      check.limit,
      check.remaining || 0,
      check.message || check.reason // Use RPC's error message
    );
  }
  
  // Limit check passed - return check details for convenience
  return check;
}

/**
 * Check if user is within limit (non-throwing)
 * 
 * Use for soft checks or UI display.
 * For hard enforcement, use requireLimit() instead.
 * 
 * @param {string} limitKey - Limit key from LIMITS constant
 * @returns {Promise<object>} Limit check result { allowed, current, limit, remaining }
 * 
 * @example
 * const invoiceCheck = await checkLimit(LIMITS.INVOICES_PER_MONTH);
 * console.log(`${invoiceCheck.current}/${invoiceCheck.limit} invoices used`);
 */
export async function checkLimit(limitKey) {
  try {
    switch (limitKey) {
      case LIMITS.INVOICES_PER_MONTH:
        return await canCreateInvoice();
        
      case LIMITS.PRODUCTS_LIFETIME:
        return await canCreateProduct();
        
      case LIMITS.CUSTOMERS_LIFETIME:
        return await canCreateCustomer();
        
      default:
        return {
          allowed: false,
          error: `Unknown limit type: ${limitKey}`
        };
    }
  } catch (error) {
    console.error(`Failed to check limit ${limitKey}:`, error);
    return {
      allowed: false,
      error: error.message
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Check if current user is authenticated
 * 
 * @returns {Promise<boolean>} True if user is logged in
 * 
 * @example
 * if (!await isAuthenticated()) {
 *   throw new Error('Authentication required');
 * }
 */
export async function isAuthenticated() {
  try {
    const userId = await getCurrentUserId();
    return !!userId;
  } catch (error) {
    return false;
  }
}

/**
 * Require user to be authenticated - throws if not logged in
 * 
 * @throws {Error} If user is not authenticated
 * 
 * @example
 * export async function createInvoice(data) {
 *   await requireAuth();  // Throws if not logged in
 *   // ... proceed
 * }
 */
export async function requireAuth() {
  const authenticated = await isAuthenticated();
  
  if (!authenticated) {
    const error = new Error('Authentication required. Please sign in to continue.');
    error.code = 'AUTHENTICATION_REQUIRED';
    throw error;
  }
}

/**
 * Get full permission context for current user
 * 
 * Useful for dashboard initialization or debug purposes.
 * Returns complete subscription + feature + limit information.
 * 
 * @returns {Promise<object>} Complete permission context
 * 
 * @example
 * const context = await getPermissionContext();
 * console.log('Tier:', context.tier.tierName);
 * console.log('Features:', context.features);
 * console.log('Limits:', context.limits);
 */
export async function getPermissionContext() {
  try {
    const tier = await getUserTier();
    const invoiceCheck = await canCreateInvoice();
    const productCheck = await canCreateProduct();
    const customerCheck = await canCreateCustomer();
    
    return {
      authenticated: await isAuthenticated(),
      tier: {
        id: tier.tierId,
        name: tier.tierName,
        level: tier.tierLevel,
        status: tier.status,
        gracePeriod: tier.gracePeriod || false
      },
      features: tier.features,
      limits: {
        invoices: {
          current: invoiceCheck.current,
          max: invoiceCheck.limit,
          remaining: invoiceCheck.remaining,
          unlimited: invoiceCheck.unlimited || false
        },
        products: {
          current: productCheck.current,
          max: productCheck.limit,
          remaining: productCheck.remaining,
          unlimited: productCheck.unlimited || false
        },
        customers: {
          current: customerCheck.current,
          max: customerCheck.limit,
          remaining: customerCheck.remaining,
          unlimited: customerCheck.unlimited || false
        }
      }
    };
  } catch (error) {
    console.error('Failed to get permission context:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY & DOCUMENTATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EXPORTS REFERENCE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * ERROR CLASSES (3):
 * ───────────────────
 * • PermissionDeniedError - Feature not available for user's tier
 * • LimitExceededError - Usage quota exceeded
 * • SubscriptionInactiveError - Subscription is inactive/expired
 * 
 * TIER RETRIEVAL (1):
 * ────────────────────
 * • getUserTier() → object - Get user's subscription with status handling
 * 
 * BOOLEAN CHECKS (2):
 * ───────────────────
 * • hasFeature(featureKey) → boolean - Check feature access (non-throwing)
 * • hasTierLevel(minLevel) → boolean - Check tier level (non-throwing)
 * 
 * ENFORCED CHECKS (3):
 * ────────────────────
 * • requireFeature(featureKey) - Throw if feature not available
 * • requireTierLevel(minLevel) - Throw if tier level insufficient
 * • requireLimit(limitKey) - Throw if usage limit exceeded
 * 
 * LIMIT CHECKS (1):
 * ─────────────────
 * • checkLimit(limitKey) → object - Soft limit check (non-throwing)
 * 
 * AUTHENTICATION (2):
 * ───────────────────
 * • isAuthenticated() → boolean - Check if user logged in
 * • requireAuth() - Throw if not authenticated
 * 
 * UTILITY (1):
 * ────────────
 * • getPermissionContext() → object - Full permission snapshot
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * USAGE PATTERNS
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * PATTERN 1: Feature-Based Protection
 * ────────────────────────────────────
 * import { requireFeature } from '../core/permissions.js';
 * import { FEATURES } from '../features/featureList.js';
 * 
 * export async function exportToCSV(data) {
 *   await requireFeature(FEATURES.CSV_EXPORT);
 *   // ... export logic
 * }
 * 
 * PATTERN 2: Quota Enforcement
 * ────────────────────────────
 * import { requireLimit } from '../core/permissions.js';
 * import { LIMITS } from '../features/featureList.js';
 * 
 * export async function createProduct(data) {
 *   await requireLimit(LIMITS.PRODUCTS_LIFETIME);
 *   // ... creation logic
 * }
 * 
 * PATTERN 3: Combined Checks
 * ──────────────────────────
 * export async function createInvoice(data) {
 *   await requireFeature(FEATURES.CLOUD_DATABASE);
 *   await requireLimit(LIMITS.INVOICES_PER_MONTH);
 *   // ... creation logic
 * }
 * 
 * PATTERN 4: Error Handling
 * ─────────────────────────
 * try {
 *   await createInvoice(data);
 * } catch (error) {
 *   if (error.code === 'PERMISSION_DENIED') {
 *     showUpgradeModal(error.requiredTier);
 *   } else if (error.code === 'LIMIT_EXCEEDED') {
 *     showLimitAlert(error.current, error.max);
 *   }
 * }
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */